// New database-backed activity service to replace localStorage-based ActivityPlannerService

import { API_BASE } from '../config/api';
import AuthService from './authService';

class DatabaseActivityService {
  constructor() {
    this.baseUrl = `${API_BASE}/api`;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    return {
      ...AuthService.getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }

  // Get scheduled activities
  async getScheduledActivities(date = null) {
    try {
      const url = date
        ? `${this.baseUrl}/activities/scheduled?date=${date}`
        : `${this.baseUrl}/activities/scheduled`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      const result = await response.json();
      return result.success ? result.activities : [];
    } catch (error) {
      console.error('Error getting scheduled activities:', error);
      return [];
    }
  }

  // Get completed activities (past activities)
  async getCompletedActivities(limit = 50) {
    try {
      const response = await fetch(`${this.baseUrl}/activities/completed?limit=${limit}`, {
        headers: this.getAuthHeaders()
      });

      const result = await response.json();
      return result.success ? result.activities : [];
    } catch (error) {
      console.error('Error getting completed activities:', error);
      return [];
    }
  }

  // Get upcoming activities (next 7 days)
  async getUpcomingActivities() {
    try {
      const response = await fetch(`${this.baseUrl}/activities/upcoming`, {
        headers: this.getAuthHeaders()
      });

      const result = await response.json();
      return result.success ? result.activities : [];
    } catch (error) {
      console.error('Error getting upcoming activities:', error);
      return [];
    }
  }

  // Get trip activities
  async getTripActivities(tripId) {
    try {
      const response = await fetch(`${this.baseUrl}/activities/trip/${tripId}`, {
        headers: this.getAuthHeaders()
      });

      const result = await response.json();
      return result.success ? result.activities : [];
    } catch (error) {
      console.error('Error getting trip activities:', error);
      return [];
    }
  }

  // Complete an activity
  async completeActivity(activityId, rating = null, feedback = '') {
    try {
      const response = await fetch(`${this.baseUrl}/activities/${activityId}/complete`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ rating, feedback })
      });

      return await response.json();
    } catch (error) {
      console.error('Error completing activity:', error);
      return { success: false, error: 'Failed to complete activity' };
    }
  }

  // Update an activity
  async updateActivity(activityId, updates) {
    try {
      const response = await fetch(`${this.baseUrl}/activities/${activityId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      return await response.json();
    } catch (error) {
      console.error('Error updating activity:', error);
      return { success: false, error: 'Failed to update activity' };
    }
  }

  // Delete an activity
  async deleteActivity(activityId) {
    try {
      const response = await fetch(`${this.baseUrl}/activities/${activityId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      console.error('Error deleting activity:', error);
      return { success: false, error: 'Failed to delete activity' };
    }
  }

  // Get user activity statistics
  async getUserStats() {
    try {
      const response = await fetch(`${this.baseUrl}/activities/stats/user`, {
        headers: this.getAuthHeaders()
      });

      const result = await response.json();
      return result.success ? result.stats : null;
    } catch (error) {
      console.error('Error getting user stats:', error);
      return null;
    }
  }

  // Helper methods for backward compatibility with existing components

  // Legacy method names for compatibility
  async getPastActivities() {
    return await this.getCompletedActivities();
  }

  async getActivitiesForDate(date) {
    return await this.getScheduledActivities(date);
  }

  // Legacy trip methods (these might need to be updated based on your trip system)
  getTrips() {
    // This would need to be implemented based on your existing trip service
    // For now, return empty array
    return [];
  }

  // Legacy stats method
  async getActivityStats() {
    const stats = await this.getUserStats();
    if (!stats) return null;

    return {
      totalCompleted: stats.completed_count,
      totalScheduled: stats.scheduled_count,
      totalSaved: 0, // This would come from saved places
      averageRating: stats.avg_rating || 0,
      totalActivities: stats.total_activities
    };
  }
}

export default DatabaseActivityService;